import React, { useState, useEffect } from 'react';

// Try importing from react-icons; fallback to high-detail React SVG icons
let GiSoccerBall, GiCricketBat, GiTennisBall, FaFutbol;
try {
  const gi = require('react-icons/gi');
  GiSoccerBall = gi.GiSoccerBall;
  GiCricketBat = gi.GiCricketBat;
  GiTennisBall = gi.GiTennisBall;
} catch (e) {}

try {
  const fa = require('react-icons/fa6');
  FaFutbol = fa.FaFutbol;
} catch (e) {}

// ── PURE GREEN CUSTOM SPORTS SVG ICONS ──
const GreenFootball = ({ size = 68 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="46" fill="#F4F9F4" stroke="#084734" strokeWidth="4" />
    <polygon points="50,32 38,41 43,55 57,55 62,41" fill="#084734" />
    <polygon points="50,10 40,17 50,32 60,17" fill="#084734" opacity="0.8" />
    <polygon points="12,42 22,48 38,41 34,26" fill="#084734" opacity="0.8" />
    <polygon points="88,42 78,48 62,41 66,26" fill="#084734" opacity="0.8" />
    <polygon points="26,82 34,70 43,55 28,60" fill="#084734" opacity="0.8" />
    <polygon points="74,82 66,70 57,55 72,60" fill="#084734" opacity="0.8" />
    <path d="M50 32V10 M38 41L22 48 M43 55L34 70 M57 55L66 70 M62 41L78 48" stroke="#084734" strokeWidth="3" />
  </svg>
);

const GreenCricket = ({ size = 68 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="46" fill="#084734" stroke="#8ECA25" strokeWidth="4" />
    <path d="M50 4 C 30 25, 30 75, 50 96" stroke="#8ECA25" strokeWidth="3.5" strokeDasharray="4 3" />
    <path d="M50 4 C 70 25, 70 75, 50 96" stroke="#8ECA25" strokeWidth="3.5" strokeDasharray="4 3" />
    <path d="M50 4 C 50 25, 50 75, 50 96" stroke="#FFFFFF" strokeWidth="2.5" />
  </svg>
);

const GreenTennis = ({ size = 68 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="46" fill="#8ECA25" stroke="#084734" strokeWidth="4" />
    <path d="M 12 28 C 38 42, 38 68, 12 82" stroke="#084734" strokeWidth="5" strokeLinecap="round" />
    <path d="M 88 28 C 62 42, 62 68, 88 82" stroke="#084734" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const SPORTS_DATA = [
  {
    name: 'Football',
    icon: (size) => FaFutbol ? <FaFutbol size={size} color="#084734" /> : <GreenFootball size={size} />,
  },
  {
    name: 'Cricket',
    icon: (size) => GiCricketBat ? <GiCricketBat size={size} color="#084734" /> : <GreenCricket size={size} />,
  },
  {
    name: 'Tennis',
    icon: (size) => GiTennisBall ? <GiTennisBall size={size} color="#8ECA25" /> : <GreenTennis size={size} />,
  },
];

export default function SportsLoader({ onFinish, durationMs = 2500 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    // Switch ball on every rotation (~800ms)
    const ballInterval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SPORTS_DATA.length);
    }, 800);

    // Fade out and finish loader after durationMs (default 2.5 seconds)
    const fadeTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, durationMs - 350);

    const finishTimer = setTimeout(() => {
      setIsVisible(false);
      if (onFinish) onFinish();
    }, durationMs);

    return () => {
      clearInterval(ballInterval);
      clearTimeout(fadeTimer);
      clearTimeout(finishTimer);
    };
  }, [durationMs, onFinish]);

  if (!isVisible) return null;

  const currentSport = SPORTS_DATA[currentIndex];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        background: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isFadingOut ? 0 : 1,
        transition: 'opacity 0.35s ease-in-out',
        pointerEvents: isFadingOut ? 'none' : 'all',
      }}
    >
      {/* Brand Logo Only */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: '900', color: '#084734', letterSpacing: '-1.5px' }}>
          Turf<span style={{ color: '#8ECA25' }}>X</span>
        </span>
      </div>

      {/* ROTATING GREEN ICON ONLY (NO CIRCLES/RINGS) */}
      <div style={{ width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div
          style={{
            width: '68px',
            height: '68px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'greenBallSpin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite',
            filter: 'drop-shadow(0 6px 12px rgba(8, 71, 52, 0.15))',
          }}
        >
          {currentSport.icon(68)}
        </div>
      </div>

      {/* KEYFRAME ANIMATIONS */}
      <style>{`
        @keyframes greenBallSpin {
          0% {
            transform: rotate(0deg) scale(0.95);
          }
          50% {
            transform: rotate(180deg) scale(1.05);
          }
          100% {
            transform: rotate(360deg) scale(0.95);
          }
        }
      `}</style>
    </div>
  );
}
